import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  setFocusedEventPayload,
  setIsTaggingInterruptedEvent,
  setIsViewingPreviousEvent,
} from "../store/slices/socket";
import { selectRecentEvent } from "../store/slices/socket/selectors";
import type { RootState } from "../store/store";
import type { IQueueEvent } from "../store/types";

/**
 * Checks if two events are the same event or related (one contains the other's UUID
 * in its contributing_events). Used to avoid false "new event" detections when the
 * same pitch is updated with additional data (e.g. hit data arriving after pitch data).
 */
const checkIfEventsAreSameOrRelated = (
  event1: IQueueEvent,
  event2: IQueueEvent,
) => {
  const sameUuid = event1.event_uuid === event2.event_uuid;
  const firstIsRelated = event1.contributing_events?.includes(
    event2.event_uuid,
  );
  const secondIsRelated = event2.contributing_events?.includes(
    event1.event_uuid,
  );
  return sameUuid || firstIsRelated || secondIsRelated;
};

/**
 * Hook that detects when a new event arrives while the user is in the middle of tagging.
 *
 * When a new event arrives during tagging, the current event is bumped to a "focused event"
 * so the user can finish tagging it, while the new event becomes the latest in the queue.
 *
 * Includes a guard to ensure the interrupted tagging only triggers when there's an actual
 * previous event being tagged (not when waiting for the first event).
 */
export const useInterruptedTagging = () => {
  const dispatch = useDispatch();
  const recentEvent = useSelector(selectRecentEvent);
  const gameState = useSelector((state: RootState) => state.game.gameState);
  const isViewingPreviousEvent = useSelector(
    (state: RootState) => state.socket.isViewingPreviousEvent,
  );
  const { type: sessionType, isTagging } = useSelector(
    (state: RootState) => state.session,
  );
  const rosters = useSelector((state: RootState) => state.game.rosters);

  const currentPitcher =
    gameState.inning.half === "top"
      ? rosters.home.currentPitcher
      : rosters.away.currentPitcher;
  const currentCatcher =
    gameState.inning.half === "top"
      ? rosters.home.currentCatcher
      : rosters.away.currentCatcher;
  const currentHitter =
    gameState.inning.half === "top"
      ? rosters.away.currentHitter
      : rosters.home.currentHitter;

  const [previousRecentEvent, setPreviousRecentEvent] = useState(recentEvent);

  useEffect(() => {
    setPreviousRecentEvent(recentEvent);
  }, [recentEvent]);

  useEffect(() => {
    if (checkIfEventsAreSameOrRelated(previousRecentEvent, recentEvent)) {
      return;
    }

    if (Object.keys(recentEvent).length > 0 && sessionType) {
      // If user is in the middle of tagging and a new event comes in,
      // bump the current event to focusedEvent so user can finish tagging there.
      // Guard: only trigger when there's an actual previous event (not waiting state).
      if (
        isTagging &&
        !isViewingPreviousEvent &&
        Object.keys(previousRecentEvent).length > 0
      ) {
        dispatch(setIsTaggingInterruptedEvent(true));
        dispatch(setIsViewingPreviousEvent(true));
        dispatch(
          setFocusedEventPayload({
            event_uuid: previousRecentEvent.event_uuid,
            gameState,
            focusedPlayers: {
              focusedPitcher: currentPitcher,
              focusedCatcher: currentCatcher,
              focusedHitter: currentHitter,
            },
          }),
        );
      }
    }
  }, [
    dispatch,
    isTagging,
    sessionType,
    isViewingPreviousEvent,
    recentEvent,
    gameState,
    currentPitcher,
    currentCatcher,
    currentHitter,
    previousRecentEvent,
  ]);
};
